import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@supabase/supabase-js";
import type { Car, Commute, LocationSuggestion, Day } from "@/types/domain";
import type { ProfileData } from "@/types/profile";
import type { CountryData } from "@/types/country";

export type AuthUser = User;

// Store version - increment when making breaking changes to persisted state structure
const STORE_VERSION = 6;
const THEME_KEY = "theme";
const GUEST_RECENT_LOCATIONS_KEY = "__guest__";
const MAX_RECENT_LOCATIONS = 10;

/** Only these keys are persisted. Theme is not persisted; the app always follows the device color scheme (see getInitialTheme). */
type PersistedSlice = Pick<
  KomutState,
  "searchFilters" | "countryData" | "recentLocationsByActor"
>;

export type PreferredRole = "rider" | "driver";
export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export interface CommuteSearchFilters {
  fromPlace: LocationSuggestion | null;
  toPlace: LocationSuggestion | null;
  commuteType: "driver" | "rider" | null; // null means both, but we invert based on preferredRole
  isOneTime: boolean | null; // null = both, true = one-time, false = recurring
  selectedDays: Day[]; // For recurring commutes
  selectedDate: string | null; // ISO date string for one-time commutes
  maxDistance: number; // in kilometers (1-100)
  // Time filters (24-hour format, HH:MM)
  leavesAfter: string | null; // Minimum leave time (e.g., "08:00")
  returnsBefore: string | null; // Maximum return time (e.g., "17:00")
  // Gender filter for commute owner
  gender: "male" | "female" | null; // null = both/all
  /** Multi-select purpose filter. Empty = all purposes. */
  purposes: string[];
  // Intent-aware search state
  selectedCommuteId: string | null; // ID of commute used for auto-search (legacy, kept for migration)
  searchMode: "auto" | "manual"; // legacy, kept for migration
  lastUsedCommuteId: string | null; // Last commute used for search (for persistence)
  /** When true, FROM was set by "Search nearby" — do not show TO field. */
  fromIsNearby: boolean;
}

export interface KomutState {
  // Persisted state (stable data)
  userInfo: AuthUser | null;
  searchFilters: CommuteSearchFilters;
  profileData: ProfileData | null;
  theme: Theme;
  countryData: CountryData | null;
  recentLocationsByActor: Record<string, LocationSuggestion[]>;

  // Non-persisted state (temporary UI state)
  commutes: Commute[];
  commuteModal: {
    isOpen: boolean;
    commute: Commute | null;
  };
  carModal: {
    isOpen: boolean;
    car: Car | null;
  };
  verifyPhoneModalOpen: boolean;
  accountSidebarOpen: boolean;

  // Internal hydration flag (not persisted)
  _hasHydrated: boolean;
  isOnline: boolean; // Connectivity state
  /** True after first client session check (useAuthGuard); used so we don't show "logged out" before we know. */
  clientSessionResolved: boolean;

  // Actions
  setUserInfo: (data: AuthUser | null) => void;
  clearUserInfo: () => void;
  setClientSessionResolved: (value: boolean) => void;
  setCommutes: (commutes: Commute[]) => void;
  setSearchFilters: (filters: Partial<CommuteSearchFilters>) => void;
  resetSearchFilters: () => void;
  setProfileData: (data: ProfileData | null) => void;
  addRecentLocation: (
    location: LocationSuggestion,
    actorId?: string | null,
  ) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setCountryData: (data: CountryData | null) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
  setIsOnline: (isOnline: boolean) => void;
  openCommuteModal: (commute?: Commute | null) => void;
  closeCommuteModal: () => void;
  openCarModal: (car?: Car | null) => void;
  closeCarModal: () => void;
  setVerifyPhoneModalOpen: (open: boolean) => void;
  setAccountSidebarOpen: (open: boolean) => void;
}

type InitialStateType = Pick<
  KomutState,
  "userInfo" | "commutes" | "searchFilters" | "theme"
>;

const defaultSearchFilters: CommuteSearchFilters = {
  fromPlace: null,
  toPlace: null,
  commuteType: null, // Will be set based on preferredRole
  isOneTime: null,
  selectedDays: [],
  selectedDate: null,
  maxDistance: 5, // Default 5km
  leavesAfter: null,
  returnsBefore: null,
  gender: null,
  purposes: [],
  selectedCommuteId: null,
  searchMode: "manual",
  lastUsedCommuteId: null,
  fromIsNearby: false,
};

function getRecentLocationActorKey(actorId?: string | null): string {
  return actorId?.trim() || GUEST_RECENT_LOCATIONS_KEY;
}

function buildRecentLocations(
  current: LocationSuggestion[],
  next: LocationSuggestion,
): LocationSuggestion[] {
  const nextId = next.id || next.displayName;
  const deduped = current.filter((entry) => {
    const entryId = entry.id || entry.displayName;
    return entryId !== nextId;
  });

  return [next, ...deduped].slice(0, MAX_RECENT_LOCATIONS);
}

/**
 * Resolve theme preference to an applied class.
 */
function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === "system") {
    if (typeof window === "undefined") return "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return theme;
}

/**
 * Theme preference at boot. The app has no user-facing theme switcher and
 * always follows the device color scheme, so this is fixed to "system".
 */
function getInitialTheme(): Theme {
  return "system";
}

const initialState: InitialStateType = {
  userInfo: null, // this is the user info with metadata and own commutes and cars.
  commutes: [], // these are the public commutes that are visible to all users
  searchFilters: defaultSearchFilters,
  theme: getInitialTheme(),
};

/**
 * Migrates stored blob to the current persisted slice (searchFilters, countryData).
 * Theme is not persisted; the app always follows the device color scheme (getInitialTheme).
 */
function migratePersistedSlice(raw: unknown): PersistedSlice {
  const state =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const sf =
    state?.searchFilters && typeof state.searchFilters === "object"
      ? (state.searchFilters as Record<string, unknown>)
      : {};

  // profileData gained phone_number_verified, phone_number_verified_at; clear so it refetches (use version < 2, not STORE_VERSION, so future bumps don’t clear)
  return {
    searchFilters: {
      ...defaultSearchFilters,
      ...sf,
      selectedDays: Array.isArray(sf.selectedDays)
        ? sf.selectedDays
        : defaultSearchFilters.selectedDays,
      maxDistance:
        typeof sf.maxDistance === "number"
          ? sf.maxDistance
          : defaultSearchFilters.maxDistance,
      fromIsNearby:
        typeof sf.fromIsNearby === "boolean"
          ? sf.fromIsNearby
          : defaultSearchFilters.fromIsNearby,
    },
    countryData: (state?.countryData ?? null) as CountryData | null,
    recentLocationsByActor:
      state?.recentLocationsByActor &&
      typeof state.recentLocationsByActor === "object"
        ? (state.recentLocationsByActor as Record<string, LocationSuggestion[]>)
        : {},
  };
}

export const useKomutStore = create<KomutState>()(
  persist(
    (set, get) => ({
      // Persisted state (initialized from defaults, will be hydrated from storage)
      ...initialState,
      profileData: null,
      countryData: null,
      recentLocationsByActor: {},

      // Non-persisted state (always starts fresh)
      commutes: [],
      commuteModal: {
        isOpen: false,
        commute: null,
      },
      carModal: {
        isOpen: false,
        car: null,
      },
      verifyPhoneModalOpen: false,
      accountSidebarOpen: false,

      // Internal hydration flag (not persisted)
      _hasHydrated: false,
      // Connectivity state (not persisted)
      isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
      clientSessionResolved: false,

      // Actions
      setUserInfo: (data) => set({ userInfo: data }),
      setClientSessionResolved: (value) =>
        set({ clientSessionResolved: value }),
      clearUserInfo: () =>
        set({
          userInfo: null,
          searchFilters: defaultSearchFilters,
          profileData: null,
          // Note: countryData is NOT cleared on sign-out
        }),
      setCountryData: (data) => set({ countryData: data }),
      addRecentLocation: (location, actorId) =>
        set((state) => {
          if (!location.id || location.id === "current-location") {
            return state;
          }

          const actorKey = getRecentLocationActorKey(
            actorId ?? state.userInfo?.id,
          );
          const currentRecent = state.recentLocationsByActor[actorKey] ?? [];

          return {
            recentLocationsByActor: {
              ...state.recentLocationsByActor,
              [actorKey]: buildRecentLocations(currentRecent, location),
            },
          };
        }),
      setCommutes: (data) =>
        set((state) => ({
          commutes: state.commutes.concat(data),
        })),
      setSearchFilters: (filters) =>
        set((state) => ({
          searchFilters: { ...state.searchFilters, ...filters },
        })),
      resetSearchFilters: () => set({ searchFilters: defaultSearchFilters }),
      setProfileData: (data) => set({ profileData: data }),
      setTheme: (theme) => {
        set({ theme });
        // Apply theme class to document immediately
        if (typeof window !== "undefined") {
          const root = window.document.documentElement;
          const resolved = resolveTheme(theme);
          root.classList.remove("light", "dark");
          root.classList.add(resolved);
          // Persist user preference (light/dark/system)
          localStorage.setItem(THEME_KEY, theme);
        }
      },
      toggleTheme: () => {
        const currentResolved = resolveTheme(get().theme);
        get().setTheme(currentResolved === "light" ? "dark" : "light");
      },
      setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),
      setIsOnline: (isOnline) => set({ isOnline }),
      openCommuteModal: (commute) =>
        set({
          commuteModal: {
            isOpen: true,
            commute: commute || null,
          },
        }),
      closeCommuteModal: () =>
        set({
          commuteModal: {
            isOpen: false,
            commute: null,
          },
        }),
      openCarModal: (car) =>
        set({
          carModal: {
            isOpen: true,
            car: car || null,
          },
        }),
      closeCarModal: () =>
        set({
          carModal: {
            isOpen: false,
            car: null,
          },
        }),
      setVerifyPhoneModalOpen: (open) => set({ verifyPhoneModalOpen: open }),
      setAccountSidebarOpen: (open) => set({ accountSidebarOpen: open }),
    }),
    {
      name: "komut-storage",
      version: STORE_VERSION,
      migrate: migratePersistedSlice,
      partialize: (state) => ({
        searchFilters: state.searchFilters,
        countryData: state.countryData,
        recentLocationsByActor: state.recentLocationsByActor,
      }),
      merge: (persisted, current) => {
        const p = persisted as PersistedSlice | undefined;
        if (!p) return current;
        // searchFilters is merged field-by-field rather than replaced wholesale:
        // a payload persisted before a filter existed would otherwise drop every
        // newer field, leaving them undefined instead of at their defaults.
        return {
          ...current,
          ...p,
          searchFilters: {
            ...defaultSearchFilters,
            ...current.searchFilters,
            ...p.searchFilters,
          },
        };
      },
      onRehydrateStorage: () => (state, error) => {
        // Mark store as hydrated after rehydration completes
        // Add timeout to prevent indefinite blocking
        if (state) {
          // Set hydrated immediately if rehydration succeeded
          state.setHasHydrated(true);
        } else if (error) {
          // If rehydration failed, still mark as hydrated to prevent white screen
          // The store will use default values
          console.warn("[Store] Rehydration failed, using defaults:", error);
          // Try to get state and set hydrated flag
          const currentState = useKomutStore.getState();
          if (currentState) {
            currentState.setHasHydrated(true);
          }
        }

        // Fallback: Force hydration after max 1 second to prevent indefinite blocking
        // This is critical for mobile where localStorage might be slow
        setTimeout(() => {
          const currentState = useKomutStore.getState();
          if (currentState && !currentState._hasHydrated) {
            console.warn("[Store] Forcing hydration after timeout");
            currentState.setHasHydrated(true);
          }
        }, 1000);
      },
    },
  ),
);
